/**
 * Utility functions for user state and configuration management
 * Centralizes logic for determining user types and generating UI configurations
 */
import { i18n } from '@/i18n';
import { SUBSCRIPTION_STATUS } from '@/utils/subscriptionStatus';

const resolveTranslator = (translateFn) =>
    typeof translateFn === 'function' ? translateFn : (key, values) => i18n.global.t(key, values);

/**
 * Get subscription message based on access level, subscription status, and date availability
 */
export function getMessage(
    isAdmin,
    endDateString,
    planName,
    status = null,
    translateFn = null
) {
    const t = resolveTranslator(translateFn);
    const hasDateAndPlan = Boolean(endDateString && planName);

    const getUpgradePrompt = () =>
        isAdmin ? t('settings.billing.subscription.messages.upgradePrompt') : '';

    const getPaymentPrompt = () =>
        isAdmin ? t('settings.billing.subscription.messages.paymentPrompt') : '';

    switch (status) {
        case SUBSCRIPTION_STATUS.TRIAL_WITH_CARD:
            return hasDateAndPlan
                ? t('settings.billing.subscription.messages.trialWithCard.withDateAndPlan', {
                      endDate: endDateString,
                      planName,
                  })
                : t('settings.billing.subscription.messages.trialWithCard.fallback');

        case SUBSCRIPTION_STATUS.TRIAL_CANCELLED: {
            const upgradePrompt = getUpgradePrompt();
            return endDateString
                ? t('settings.billing.subscription.messages.trialCancelled.withDate', {
                      endDate: endDateString,
                      upgradePrompt,
                  })
                : t('settings.billing.subscription.messages.trialCancelled.fallback', {
                      upgradePrompt,
                  });
        }

        case SUBSCRIPTION_STATUS.ACTIVE_CANCELLED: {
            const upgradePrompt = getUpgradePrompt();
            return endDateString
                ? t('settings.billing.subscription.messages.activeCancelled.withDate', {
                      endDate: endDateString,
                      upgradePrompt,
                  })
                : t('settings.billing.subscription.messages.activeCancelled.fallback', {
                      upgradePrompt,
                  });
        }
        case SUBSCRIPTION_STATUS.TRIAL_WITHOUT_CARD: {
            const paymentPrompt = getPaymentPrompt();
            return hasDateAndPlan
                ? t('settings.billing.subscription.messages.trialWithoutCard.withDateAndPlan', {
                      paymentPrompt,
                      endDate: endDateString,
                      planName,
                  })
                : t('settings.billing.subscription.messages.trialWithoutCard.fallback', {
                      paymentPrompt,
                  });
        }
        default:
            return isAdmin
                ? t('settings.billing.subscription.messages.viewOnly.admin')
                : t('settings.billing.subscription.messages.viewOnly.nonAdmin');
    }
}

/**
 * Returns configuration details for user subscription state in UI components.
 */
export function getUserSubscriptionConfig({
    subscriptionDetails,
    isCompanyAdmin,
    freeTrialEndDateString,
    planName,
    translateFn = null,
} = {}) {
    if (!subscriptionDetails) return null;

    const t = resolveTranslator(translateFn);
    const status = subscriptionDetails.status;
    const message = getMessage(
        isCompanyAdmin,
        freeTrialEndDateString,
        planName,
        status,
        t
    );

    switch (status) {
        case SUBSCRIPTION_STATUS.VIEW_ONLY:
            return {
                title: t('settings.billing.subscription.titles.viewOnly'),
                message,
                buttonText: t('settings.billing.subscription.actions.unlockFullAccess'),
                actionButtons: isCompanyAdmin
                    ? [
                          {
                              label: t('settings.billing.subscription.actions.upgradePlan'),
                              icon: 'fi-eye',
                              class: 'btn-secondary',
                              click: 'goToPlans',
                          },
                      ]
                    : [],
                type: 'WARNING',
            };

        case SUBSCRIPTION_STATUS.TRIAL_WITH_CARD:
            return {
                title: t('settings.billing.subscription.titles.trialEndingSoon'),
                message,
                buttonText: t('settings.billing.subscription.actions.viewBilling'),
                actionButtons: isCompanyAdmin
                    ? [
                          {
                              label: t('settings.billing.subscription.actions.viewBilling'),
                              icon: 'fi-eye',
                              class: 'btn-secondary',
                              click: 'goToPlans',
                          },
                      ]
                    : [],
                type: 'INFO',
            };

        case SUBSCRIPTION_STATUS.TRIAL_WITHOUT_CARD:
            return {
                title: t('settings.billing.subscription.titles.trialEndingSoon'),
                message,
                buttonText: t('settings.billing.subscription.actions.updatePaymentMethod'),
                actionButtons: isCompanyAdmin
                    ? [
                          {
                              label: t(
                                  'settings.billing.subscription.actions.updatePaymentMethod'
                              ),
                              icon: 'fi-chevron-right',
                              class: 'btn-secondary',
                              click: 'goToPlans',
                              actionType: 'openPayment',
                          },
                      ]
                    : [],
                type: 'WARNING',
            };
        case SUBSCRIPTION_STATUS.TRIAL_CANCELLED:
            return {
                title: t('settings.billing.subscription.titles.trialCancelled'),
                message,
                buttonText: t('settings.billing.subscription.actions.viewPlans'),
                actionButtons: isCompanyAdmin
                    ? [
                          {
                              label: t('settings.billing.subscription.actions.viewPlans'),
                              icon: 'fi-chevron-right',
                              class: 'btn-secondary',
                              click: 'goToPlans',
                          },
                      ]
                    : [],
                type: 'WARNING',
            };
        case SUBSCRIPTION_STATUS.ACTIVE_CANCELLED:
            return {
                title: t('settings.billing.subscription.titles.subscriptionEndingSoon'),
                message,
                buttonText: t('settings.billing.subscription.actions.viewPlans'),
                actionButtons: isCompanyAdmin
                    ? [
                          {
                              label: t('settings.billing.subscription.actions.viewPlans'),
                              icon: 'fi-chevron-right',
                              class: 'btn-secondary',
                              click: 'goToPlans',
                          },
                      ]
                    : [],
                type: 'WARNING',
            };

        default:
            return null;
    }
}

/**
 * Get free trial persistent notification configuration
 */
export function getFreeTrialPersistentNotification({
    subscriptionDetails,
    isCompanyAdmin,
    freeTrialEndDateString,
    planName,
    translateFn = null,
} = {}) {
    if (!subscriptionDetails) return null;

    const t = resolveTranslator(translateFn);
    const hasCard = Boolean(subscriptionDetails.hasCard);
    const isInTrial = Boolean(subscriptionDetails.isInTrial);

    if (!isCompanyAdmin || !isInTrial || hasCard) {
        return null;
    }

    return {
        title: t('settings.billing.subscription.titles.trialEndingSoon'),
        message: getMessage(
            isCompanyAdmin,
            freeTrialEndDateString,
            planName,
            subscriptionDetails.status,
            t
        ),
        type: 'WARNING',
        showClearBtn: false,
        actionButtons: [
            {
                label: t('settings.billing.subscription.actions.updatePaymentMethod'),
                icon: 'fi-chevron-right',
                class: 'btn-primary',
                click: 'goToPlans',
                actionType: 'openPayment',
            },
        ],
    };
}
